import { Link, useLocation } from "wouter";
import { Calendar, HelpCircle, Upload, Clock, CheckCircle2, History, ShieldAlert } from "lucide-react";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import type { LocationFreshness, StaleLocationEntry, ReportType, GeneratedAt } from "@/hooks/use-report-freshness";

function formatGeneratedAt(generatedAt: GeneratedAt | null | undefined): string {
  if (!generatedAt) return "";
  if (generatedAt.confidence === 'exact' && generatedAt.timestamp) {
    return format(new Date(generatedAt.timestamp), "MMM d, yyyy 'at' h:mm a");
  }
  try {
    const dateStr = generatedAt.date + "T00:00:00";
    return format(new Date(dateStr), "MMM d, yyyy") + ", time unknown";
  } catch {
    return generatedAt.date + ", time unknown";
  }
}

interface ReportFreshnessModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  staleLocations: (LocationFreshness | StaleLocationEntry)[];
  locationsWithGaps: (LocationFreshness | StaleLocationEntry)[];
  reportTypes: ReportType[];
  isCriticallyStale?: boolean;
  allLocations?: LocationFreshness[];
  allPetsLocations?: LocationFreshness[];
  interactionLocations?: LocationFreshness[];
  dataStartDate?: string | null;
}

function getReportTypeLabel(reportTypes: ReportType[]): string {
  if (reportTypes.length === 2) {
    return "All Pets and Interaction Summary reports";
  }
  if (reportTypes.includes("allPets")) {
    return "All Pets reports";
  }
  return "Interaction Summary reports";
}

function FooterButtons({ onClose, isMobile }: { onClose: () => void; isMobile: boolean }) {
  const [location, setLocation] = useLocation();
  const isOnReportsPage = location === "/reports";

  const navigateToUpload = (tab: string) => {
    onClose();
    const url = `/reports?tab=${tab}&upload=true`;
    if (isOnReportsPage) {
      window.history.pushState(null, "", url);
      window.dispatchEvent(new PopStateEvent("popstate"));
    } else {
      setLocation(url);
    }
  };

  if (isOnReportsPage) {
    return (
      <div className={`pt-4 border-t mt-4 flex ${isMobile ? "flex-col" : "flex-row justify-end"} gap-2`}>
        <Button
          variant="outline"
          onClick={onClose}
          className={isMobile ? "w-full" : ""}
          data-testid="button-close-freshness-modal"
        >
          Close
        </Button>
        <Button
          variant="outline"
          className={isMobile ? "w-full" : ""}
          onClick={() => navigateToUpload("all-pets")}
          data-testid="button-upload-all-pets"
        >
          <Upload className="h-4 w-4 mr-2" />
          Upload All Pets
        </Button>
        <Button
          className={isMobile ? "w-full" : ""}
          onClick={() => navigateToUpload("interactions")}
          data-testid="button-upload-dis"
        >
          <Upload className="h-4 w-4 mr-2" />
          Upload DIS
        </Button>
      </div>
    );
  }

  return (
    <div className={`pt-4 border-t mt-4 flex ${isMobile ? "flex-col" : "flex-row justify-end"} gap-2`}>
      <Button
        variant="outline"
        onClick={onClose}
        className={isMobile ? "w-full" : ""}
        data-testid="button-close-freshness-modal"
      >
        Close
      </Button>
      <Button asChild className={isMobile ? "w-full" : ""} onClick={onClose}>
        <Link to="/reports" data-testid="link-view-reports">
          View Reports
        </Link>
      </Button>
    </div>
  );
}

function getReportTypeSectionLabel(type: ReportType): string {
  return type === "allPets" ? "All Pets" : "Daily Interaction Summary";
}

function hasSourceReportType(loc: LocationFreshness | StaleLocationEntry): loc is StaleLocationEntry {
  return "sourceReportType" in loc;
}

function groupByReportType(entries: (LocationFreshness | StaleLocationEntry)[]): Map<ReportType | "unknown", (LocationFreshness | StaleLocationEntry)[]> {
  const groups = new Map<ReportType | "unknown", (LocationFreshness | StaleLocationEntry)[]>();
  for (const entry of entries) {
    const type = hasSourceReportType(entry) ? entry.sourceReportType : "unknown";
    if (!groups.has(type)) {
      groups.set(type, []);
    }
    groups.get(type)!.push(entry);
  }
  return groups;
}

function LocationStatusRow({
  loc,
  staleSet,
  testIdPrefix,
  isCriticallyStale,
}: {
  loc: LocationFreshness;
  staleSet: Set<string>;
  testIdPrefix: string;
  isCriticallyStale: boolean;
}) {
  const isStale = staleSet.has(loc.locationCode);
  const generatedStr = formatGeneratedAt(loc.generatedAt);

  if (!isStale) {
    return (
      <li
        className="text-sm"
        data-testid={`current-location-${testIdPrefix}-${loc.locationCode}`}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium">{loc.locationLabel}</span>
          <span className="text-emerald-600 dark:text-emerald-500 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            Current
          </span>
        </div>
        {generatedStr && (
          <div className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground">
            <Clock className="h-3 w-3 flex-shrink-0" />
            <span>Received: {generatedStr}</span>
          </div>
        )}
      </li>
    );
  }

  const staleColor = isCriticallyStale
    ? "text-red-600 dark:text-red-400"
    : "text-amber-600 dark:text-amber-500";

  return (
    <li
      className="text-sm"
      data-testid={`stale-location-${testIdPrefix}-${loc.locationCode}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium">{loc.locationLabel}</span>
        <span className={staleColor}>
          — {loc.staleDays === 1 ? "1 day" : `${loc.staleDays} days`} ago
        </span>
      </div>
      {generatedStr && (
        <div className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground">
          <Clock className="h-3 w-3 flex-shrink-0" />
          <span>Received: {generatedStr}</span>
        </div>
      )}
    </li>
  );
}

function GapLocationList({ locs, typeKey }: { locs: (LocationFreshness | StaleLocationEntry)[]; typeKey: string }) {
  return (
    <ul className="space-y-1">
      {locs.map((loc) => {
        const count = loc.missingCountTotal ?? 0;
        const countColor = count > 2
          ? "text-red-600 dark:text-red-400"
          : "text-amber-600 dark:text-amber-500";
        return (
          <li
            key={`${typeKey}-${loc.locationCode}`}
            className="text-sm flex items-center gap-2 flex-wrap"
            data-testid={`gap-location-${typeKey}-${loc.locationCode}`}
          >
            <span className="font-medium">{loc.locationLabel}</span>
            <span className="text-muted-foreground">—</span>
            <span className={countColor}>
              {count} missing {count === 1 ? "day" : "days"}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function ModalContent({
  staleLocations,
  locationsWithGaps,
  reportTypes,
  onClose,
  isCriticallyStale = false,
  allLocations,
  allPetsLocations,
  interactionLocations,
  dataStartDate,
}: {
  staleLocations: (LocationFreshness | StaleLocationEntry)[];
  locationsWithGaps: (LocationFreshness | StaleLocationEntry)[];
  reportTypes: ReportType[];
  onClose: () => void;
  isCriticallyStale?: boolean;
  allLocations?: LocationFreshness[];
  allPetsLocations?: LocationFreshness[];
  interactionLocations?: LocationFreshness[];
  dataStartDate?: string | null;
}) {
  const isMobile = useIsMobile();
  const gapGroups = groupByReportType(locationsWithGaps);

  // P28a: Surface ingest gate notice inside the data freshness dialog
  const { data: metaData } = useQuery<{ ingestHoldEnabled: boolean; ingestHoldReason?: string | null }>({
    queryKey: ["/api/app-metadata"],
  });

  // Build a set of stale location codes per report type for fast lookup
  const allPetsStaleSet = new Set<string>();
  const interactionStaleSet = new Set<string>();
  for (const loc of staleLocations) {
    if (hasSourceReportType(loc)) {
      if (loc.sourceReportType === "allPets") allPetsStaleSet.add(loc.locationCode);
      else interactionStaleSet.add(loc.locationCode);
    } else {
      // Single-type mode — all stale entries go to the shared set
      allPetsStaleSet.add(loc.locationCode);
    }
  }

  const isBothTypes = !!(allPetsLocations && interactionLocations);
  const showAllLocations = isBothTypes || !!allLocations;

  return (
    <div className="flex flex-col h-full">
      {metaData?.ingestHoldEnabled && (
        <div
          className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300"
          data-testid="banner-ingest-gate-freshness"
        >
          <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
          <p className="text-sm">
            <span className="font-semibold">Report ingestion is currently disabled.</span>{" "}
            {metaData.ingestHoldReason
              ? metaData.ingestHoldReason + " "
              : "New PDF reports are not being processed automatically. "}
            Contact a super admin for details.
          </p>
        </div>
      )}

      <ScrollArea className={isMobile ? "h-[calc(100dvh-10rem)]" : "max-h-[60vh]"}>
        <div className="space-y-4 pr-4">
          <p className="text-sm text-muted-foreground">
            Data derived from {getReportTypeLabel(reportTypes)}.
          </p>

          {/* Current Reports — shows all locations with stale/current status */}
          {showAllLocations && (
            <div className="space-y-3 pt-1 border-t">
              <div className="flex items-center gap-2 text-foreground">
                <Clock className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                <span className="font-medium text-sm">Current Reports</span>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild data-testid="tooltip-trigger-current-reports">
                    <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help flex-shrink-0" />
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs">
                    <p className="text-sm">
                      Shows the date each All Pets and Daily Interaction Summary report was generated, as recorded in the report or the email it arrived in. Locations are marked stale if no report has been received for that day.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>

              {isBothTypes ? (
                <div
                  className={`${isMobile ? "space-y-4" : "grid grid-cols-2 gap-x-6"} pl-6`}
                  data-testid="list-current-reports"
                >
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">All Pets</p>
                    <ul className="space-y-1.5">
                      {allPetsLocations!.map((loc) => (
                        <LocationStatusRow
                          key={loc.locationCode}
                          loc={loc}
                          staleSet={allPetsStaleSet}
                          testIdPrefix="allpets"
                          isCriticallyStale={isCriticallyStale}
                        />
                      ))}
                    </ul>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Daily Interaction Summary</p>
                    <ul className="space-y-1.5">
                      {interactionLocations!.map((loc) => (
                        <LocationStatusRow
                          key={loc.locationCode}
                          loc={loc}
                          staleSet={interactionStaleSet}
                          testIdPrefix="interaction"
                          isCriticallyStale={isCriticallyStale}
                        />
                      ))}
                    </ul>
                  </div>
                </div>
              ) : (
                <div
                  className={`${isMobile ? "space-y-2" : "grid grid-cols-2 gap-x-6 gap-y-2"} pl-6`}
                  data-testid="list-current-reports"
                >
                  <ul className="space-y-1.5">
                    {(allLocations ?? []).map((loc) => (
                      <LocationStatusRow
                        key={loc.locationCode}
                        loc={loc}
                        staleSet={allPetsStaleSet}
                        testIdPrefix="single"
                        isCriticallyStale={isCriticallyStale}
                      />
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Historical Gaps */}
          {locationsWithGaps.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-foreground">
                <Calendar className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                <span className="font-medium text-sm">Historical Gaps</span>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger data-testid="tooltip-trigger-gaps">
                    <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <p className="text-sm">
                      Gaps are days in the past where expected reports were not received.
                      This may affect historical data accuracy and trend analysis.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              {reportTypes.length > 1 ? (
                isMobile ? (
                  <div className="space-y-4 pl-6" data-testid="list-gap-locations">
                    {Array.from(gapGroups.entries()).map(([type, locs]) => (
                      <div key={type} className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          {type === "unknown" ? "Reports" : getReportTypeSectionLabel(type as ReportType)}
                        </p>
                        <GapLocationList locs={locs} typeKey={String(type)} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4 pl-6" data-testid="list-gap-locations">
                    {Array.from(gapGroups.entries()).map(([type, locs]) => (
                      <div key={type} className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          {type === "unknown" ? "Reports" : getReportTypeSectionLabel(type as ReportType)}
                        </p>
                        <GapLocationList locs={locs} typeKey={String(type)} />
                      </div>
                    ))}
                  </div>
                )
              ) : (
                <ul className="space-y-1 pl-6" data-testid="list-gap-locations">
                  {locationsWithGaps.map((loc) => {
                    const count = loc.missingCountTotal ?? 0;
                    const countColor = count > 2
                      ? "text-red-600 dark:text-red-400"
                      : "text-amber-600 dark:text-amber-500";
                    return (
                      <li
                        key={loc.locationCode}
                        className="text-sm flex items-center gap-2 flex-wrap"
                        data-testid={`gap-location-${loc.locationCode}`}
                      >
                        <span className="font-medium">{loc.locationLabel}</span>
                        <span className="text-muted-foreground">—</span>
                        <span className={countColor}>
                          {count} missing {count === 1 ? "day" : "days"}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {!showAllLocations && staleLocations.length === 0 && locationsWithGaps.length === 0 && (
            <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-500">
              <CheckCircle2 className="h-4 w-4" />
              <span className="text-sm">All data is current.</span>
            </div>
          )}

          {dataStartDate && (
            <div
              className="flex items-center gap-3 pt-1"
              data-testid="data-start-date-row"
            >
              <div className="flex-1 border-t border-dashed border-muted-foreground/30" />
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
                <History className="h-3 w-3 flex-shrink-0" />
                <span>
                  BETI records begin{" "}
                  {format(new Date(dataStartDate + "T00:00:00"), "MMM d, yyyy")}
                </span>
              </div>
              <div className="flex-1 border-t border-dashed border-muted-foreground/30" />
            </div>
          )}
        </div>
      </ScrollArea>

      <FooterButtons onClose={onClose} isMobile={isMobile} />
    </div>
  );
}

export function ReportFreshnessModal({
  open,
  onOpenChange,
  staleLocations,
  locationsWithGaps,
  reportTypes,
  isCriticallyStale = false,
  allLocations,
  allPetsLocations,
  interactionLocations,
  dataStartDate,
}: ReportFreshnessModalProps) {
  const isMobile = useIsMobile();

  const handleClose = () => onOpenChange(false);

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          className="h-[100dvh] flex flex-col rounded-t-none"
          data-testid="sheet-report-freshness"
        >
          <SheetHeader>
            <SheetTitle>Data Status</SheetTitle>
          </SheetHeader>
          <div className="flex-1 mt-4 overflow-hidden">
            <ModalContent
              staleLocations={staleLocations}
              locationsWithGaps={locationsWithGaps}
              reportTypes={reportTypes}
              onClose={handleClose}
              isCriticallyStale={isCriticallyStale}
              allLocations={allLocations}
              allPetsLocations={allPetsLocations}
              interactionLocations={interactionLocations}
              dataStartDate={dataStartDate}
            />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="dialog-report-freshness">
        <DialogHeader>
          <DialogTitle>Data Status</DialogTitle>
        </DialogHeader>
        <ModalContent
          staleLocations={staleLocations}
          locationsWithGaps={locationsWithGaps}
          reportTypes={reportTypes}
          onClose={handleClose}
          isCriticallyStale={isCriticallyStale}
          allLocations={allLocations}
          allPetsLocations={allPetsLocations}
          interactionLocations={interactionLocations}
          dataStartDate={dataStartDate}
        />
      </DialogContent>
    </Dialog>
  );
}
